import { IconButton } from '@material-ui/core'
import { Edit, InfoOutlined } from '@material-ui/icons'
import { ColDef } from 'ag-grid-community'
import { History } from 'history'
import React from 'react'
import { PATHS, resolvePathParams } from '../../../application-paths'
import { pathWithQueryParams, supportsFullscreenEdit } from '../../../services'
import { ReferenceField, RolesField } from '../../content-list'
import { createAnchorFromName, SETUP_DOCS_URL, UpdatedSettings } from '../../settings/settings-table'
import { ActionFormatter } from '../Formatters/ActionFormatter'
import { DateTimeFormatter, formatDateTime } from '../Formatters/DateTimeFormatter'
import { EditBinaryFormatter } from '../Formatters/EditBinaryFormatter'
import { IconFormatter } from '../Formatters/IconFormatter'
import { UserNameFormatter } from '../Formatters/UserNameFormatter'

const enabledColDef: ColDef = {
  headerName: 'Enabled',
  field: 'Enabled',
  headerTooltip: 'Enabled',
  tooltipField: 'Enabled',
  width: 70,
  maxWidth: 70,
  minWidth: 70,
}

const lockedColDef: ColDef = {
  headerName: 'Locked',
  field: 'Locked',
  headerTooltip: 'Locked',
  tooltipField: 'Locked',
  width: 70,
  maxWidth: 70,
  minWidth: 70,
}

const checkBoxCol: ColDef = {
  headerCheckboxSelection: true,
  checkboxSelection: true,
  headerCheckboxSelectionFilteredOnly: true,
  width: 27,
  minWidth: 27,
  cellStyle: { padding: '0px 4px' },
  headerClass: 'grid-checkbox-header',
}
const iconCol: ColDef = {
  headerName: '',
  field: 'Icon',
  width: 44,
  minWidth: 44,
  maxWidth: 44,
  cellRenderer: IconFormatter,
  cellStyle: { padding: '0 6px', display: 'flex', alignItems: 'center' },
}
const idCol: ColDef = {
  headerName: 'ID',
  field: 'Id',
  headerTooltip: 'ID',
  tooltipField: 'Id',
  flex: 0.75,
  filter: true,
  sortable: true,
  resizable: true,
  cellStyle: { paddingRight: 0 },
}

export const contentColumnDefs: ColDef[] = [
  checkBoxCol,
  iconCol,
  idCol,
  {
    headerName: 'Display Name',
    field: 'DisplayName',
    headerTooltip: 'Display Name',
    tooltipField: 'DisplayName',
    flex: 5,
    filter: true,
    sortable: true,
    comparator: (valueA: string, valueB: string) => {
      return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
    },
    resizable: true,
  },
  {
    headerName: 'Name',
    field: 'Name',
    headerTooltip: 'Name',
    tooltipField: 'Name',
    flex: 1.5,
    filter: true,
    sortable: true,
    comparator: (valueA: string, valueB: string) => {
      return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
    },
    resizable: true,
  },
  {
    headerName: 'Index',
    field: 'Index',
    headerTooltip: 'Index',
    tooltipField: 'Index',
    flex: 0.75,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Created By',
    field: 'CreatedBy',
    headerTooltip: 'Created By',
    cellRenderer: UserNameFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Creation Date',
    field: 'CreationDate',
    headerTooltip: 'Creation Date',
    cellRenderer: DateTimeFormatter,
    tooltipValueGetter: (params) => formatDateTime(params.value),
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Modified By',
    field: 'ModifiedBy',
    headerTooltip: 'Modified By',
    cellRenderer: UserNameFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Modification Date',
    field: 'ModificationDate',
    headerTooltip: 'Modification Date',
    cellRenderer: DateTimeFormatter,
    tooltipValueGetter: (params) => formatDateTime(params.value),
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Actions',
    field: 'Actions',
    headerTooltip: 'Actions',
    cellRenderer: ActionFormatter,
    width: 70,
    resizable: false,
    wrapText: true,
    autoHeight: true,
  },
]

export const userColumnDefs: ColDef[] = [
  checkBoxCol,
  iconCol,
  {
    headerName: 'Display Name',
    field: 'DisplayName',
    headerTooltip: 'Display Name',
    tooltipField: 'DisplayName',
    filter: true,
    sortable: true,
    comparator: (valueA: string, valueB: string) => {
      return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
    },
    resizable: true,
  },
  {
    headerName: 'E-mail',
    field: 'Email',
    headerTooltip: 'E-mail',
    tooltipField: 'Email',
  },
  {
    headerName: 'Roles',
    field: 'AllRoles',
    headerTooltip: 'Roles',
    width: 110,
    minWidth: 110,
    maxWidth: 110,
    cellRenderer: (params: any) => {
      const roles = Array.isArray(params.data.AllRoles) ? params.data.AllRoles : []
      const directRoles = Array.isArray(params.data.DirectRoles) ? params.data.DirectRoles : []
      return <RolesField user={params.data} roles={roles} directRoles={directRoles} />
    },
  },
  enabledColDef,
  {
    headerName: 'Actions',
    field: 'Actions',
    headerTooltip: 'Actions',
    cellRenderer: ActionFormatter,
    width: 70,
    resizable: false,
    wrapText: true,
    autoHeight: true,
  },
]

export const groupColumnDefs: ColDef[] = [
  checkBoxCol,
  iconCol,
  {
    headerName: 'Display Name',
    field: 'DisplayName',
    headerTooltip: 'Display Name',
    tooltipField: 'DisplayName',
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Description',
    field: 'Description',
    headerTooltip: 'Description',
    tooltipField: 'Description',
    resizable: true,
  },
  {
    headerName: 'Members',
    field: 'Members',
    headerTooltip: 'Members',
    sortable: true,
    width: 110,
    minWidth: 110,
    maxWidth: 110,
    cellRenderer: (params: any) => {
      return <ReferenceField content={params.data} fieldName={'Members'} parent={params.data} showIcon={true} />
    },
  },
  {
    headerName: 'Actions',
    field: 'Actions',
    cellRenderer: ActionFormatter,
    width: 70,
    resizable: false,
  },
]

export const savedQueriesColumnDefs: ColDef[] = [
  iconCol,
  {
    headerName: 'Display Name',
    field: 'DisplayName',
    headerTooltip: 'Display Name',
    tooltipField: 'DisplayName',
    sortable: true,
    resizable: true,
  },
  lockedColDef,
  {
    headerName: 'Created By',
    field: 'CreatedBy',
    headerTooltip: 'Created By',
    cellRenderer: UserNameFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Actions',
    field: 'Actions',
    cellRenderer: ActionFormatter,
    width: 70,
    resizable: false,
  },
]

export const trashColumnDefs: ColDef[] = [
  iconCol,
  {
    headerName: 'Display Name',
    field: 'DisplayName',
    headerTooltip: 'Display Name',
    tooltipField: 'DisplayName',
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Deleted From',
    field: 'OriginalPath',
    headerTooltip: 'Deleted From',
    tooltipField: 'OriginalPath',
    flex: 2,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Deleted By',
    field: 'CreatedBy',
    headerTooltip: 'Deleted By',
    cellRenderer: UserNameFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Deleted When',
    field: 'CreationDate',
    headerTooltip: 'Deleted When',
    tooltipValueGetter: (params) => formatDateTime(params.value),
    cellRenderer: DateTimeFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Actions',
    field: 'Actions',
    cellRenderer: ActionFormatter,
    width: 70,
    resizable: false,
  },
]

export const contentExplorerColumnDefs: ColDef[] = [
  checkBoxCol,
  iconCol,
  {
    headerName: 'Name',
    field: 'Name',
    headerTooltip: 'Name',
    tooltipField: 'Name',
    flex: 1.5,
    filter: true,
    sortable: true,
    comparator: (valueA: string, valueB: string) => {
      return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
    },
    resizable: true,
  },
  {
    headerName: 'Display Name',
    field: 'DisplayName',
    headerTooltip: 'Display Name',
    tooltipField: 'DisplayName',
    flex: 5,
    filter: true,
    sortable: true,
    comparator: (valueA: string, valueB: string) => {
      return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
    },
    resizable: true,
  },
  lockedColDef,
  {
    headerName: 'Created By',
    field: 'CreatedBy',
    headerTooltip: 'Created By',
    cellRenderer: UserNameFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Creation Date',
    field: 'CreationDate',
    headerTooltip: 'Creation Date',
    tooltipValueGetter: (params) => formatDateTime(params.value),
    cellRenderer: DateTimeFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Modified By',
    field: 'ModifiedBy',
    headerTooltip: 'Modified By',
    cellRenderer: UserNameFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Modification Date',
    field: 'ModificationDate',
    headerTooltip: 'Modification Date',
    tooltipValueGetter: (params) => formatDateTime(params.value),
    cellRenderer: DateTimeFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Actions',
    field: 'Actions',
    headerTooltip: 'Actions',
    cellRenderer: ActionFormatter,
    width: 70,
    resizable: false,
    wrapText: true,
    autoHeight: true,
  },
]

export const contentTypesColumnDefs: ColDef[] = [
  checkBoxCol,
  iconCol,
  {
    headerName: 'Display Name',
    field: 'DisplayName',
    headerTooltip: 'Display Name',
    tooltipField: 'DisplayName',
    flex: 5,
    filter: true,
    sortable: true,
    comparator: (valueA: string, valueB: string) => {
      return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
    },
    resizable: true,
  },
  {
    headerName: 'Name',
    field: 'Name',
    headerTooltip: 'Name',
    tooltipField: 'Name',
    flex: 1.5,
    filter: true,
    sortable: true,
    comparator: (valueA: string, valueB: string) => {
      return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
    },
    resizable: true,
  },
  {
    colId: 'edit-binary',
    headerName: 'Edit',
    headerTooltip: 'Edit XML',
    width: 48,
    minWidth: 48,
    maxWidth: 48,
    sortable: false,
    filter: false,
    resizable: false,
    suppressMovable: true,
    cellStyle: { padding: '0 10px', display: 'flex', alignItems: 'center' },
    cellRenderer: EditBinaryFormatter,
  },
  {
    headerName: 'Description',
    field: 'Description',
    headerTooltip: 'Description',
    tooltipField: 'Description',
    resizable: true,
  },
  {
    headerName: 'ParentTypeName',
    field: 'ParentTypeName',
    headerTooltip: 'ParentTypeName',
    tooltipField: 'ParentTypeName',
    resizable: true,
  },
  {
    headerName: 'Modified By',
    field: 'ModifiedBy',
    headerTooltip: 'Modified By',
    cellRenderer: UserNameFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Modification Date',
    field: 'ModificationDate',
    headerTooltip: 'Modification Date',
    tooltipValueGetter: (params) => formatDateTime(params.value),
    cellRenderer: DateTimeFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Actions',
    field: 'Actions',
    headerTooltip: 'Actions',
    cellRenderer: ActionFormatter,
    width: 70,
    resizable: false,
    wrapText: true,
    autoHeight: true,
  },
]

export const contentTemplatesColumnDefs: ColDef[] = [
  checkBoxCol,
  iconCol,
  {
    headerName: 'Display Name',
    field: 'DisplayName',
    headerTooltip: 'Display Name',
    tooltipField: 'DisplayName',
    flex: 5,
    filter: true,
    sortable: true,
    comparator: (valueA: string, valueB: string) => {
      return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
    },
    resizable: true,
  },
  lockedColDef,

  {
    headerName: 'Created By',
    field: 'CreatedBy',
    headerTooltip: 'Created By',
    cellRenderer: UserNameFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Creation Date',
    field: 'CreationDate',
    headerTooltip: 'Creation Date',
    tooltipValueGetter: (params) => formatDateTime(params.value),
    cellRenderer: DateTimeFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Modified By',
    field: 'ModifiedBy',
    headerTooltip: 'Modified By',
    cellRenderer: UserNameFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Modification Date',
    field: 'ModificationDate',
    headerTooltip: 'Modification Date',
    tooltipValueGetter: (params) => formatDateTime(params.value),
    cellRenderer: DateTimeFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Actions',
    field: 'Actions',
    headerTooltip: 'Actions',
    cellRenderer: ActionFormatter,
    width: 70,
    resizable: false,
    wrapText: true,
    autoHeight: true,
  },
]

const getBinaryEditColumn = (
  history: History,
  appPath: typeof PATHS.configuration.appPath | typeof PATHS.localization.appPath,
): ColDef => ({
  headerName: 'Edit',
  resizable: false,
  width: 50,
  cellRenderer: (params: any) =>
    supportsFullscreenEdit(params.data) ? (
      <IconButton
        aria-label={`Edit ${params.data?.DisplayName || params.data?.Name || ''} in full screen`}
        style={{ padding: '3px', marginBottom: '6px' }}
        onClick={(event) => {
          event.stopPropagation()
          history.push(
            pathWithQueryParams({
              path: resolvePathParams({ path: appPath, params: { action: 'edit-binary' } }),
              newParams: { path: '', content: `/${params.data?.Name || ''}` },
            }),
          )
        }}>
        <Edit style={{ fontSize: '16px' }} />
      </IconButton>
    ) : null,
})

export const getLocalizationColumnDefs = (history: History): ColDef[] => [
  checkBoxCol,
  iconCol,
  {
    headerName: 'Display Name',
    field: 'DisplayName',
    headerTooltip: 'Display Name',
    tooltipField: 'DisplayName',
    flex: 5,
    filter: true,
    sortable: true,
    comparator: (valueA: string, valueB: string) => {
      return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
    },
    resizable: true,
  },
  lockedColDef,
  getBinaryEditColumn(history, PATHS.localization.appPath),

  {
    headerName: 'Created By',
    field: 'CreatedBy',
    headerTooltip: 'Created By',
    cellRenderer: UserNameFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Creation Date',
    field: 'CreationDate',
    headerTooltip: 'Creation Date',
    tooltipValueGetter: (params) => formatDateTime(params.value),
    cellRenderer: DateTimeFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Modified By',
    field: 'ModifiedBy',
    headerTooltip: 'Modified By',
    cellRenderer: UserNameFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Modification Date',
    field: 'ModificationDate',
    headerTooltip: 'Modification Date',
    tooltipValueGetter: (params) => formatDateTime(params.value),
    cellRenderer: DateTimeFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Actions',
    field: 'Actions',
    headerTooltip: 'Actions',
    cellRenderer: ActionFormatter,
    width: 70,
    resizable: false,
    wrapText: true,
    autoHeight: true,
  },
]

export const webHooksColumnDefs: ColDef[] = [
  checkBoxCol,
  iconCol,
  {
    headerName: 'Display Name',
    field: 'DisplayName',
    headerTooltip: 'Display Name',
    tooltipField: 'DisplayName',
    flex: 5,
    filter: true,
    sortable: true,
    comparator: (valueA: string, valueB: string) => {
      return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
    },
    resizable: true,
  },
  {
    headerName: 'WebHookUrl',
    field: 'WebHookUrl',
    headerTooltip: 'WebHookUrl',
    tooltipField: 'WebHookUrl',
    resizable: true,
  },
  enabledColDef,
  {
    headerName: 'SuccessfulCalls',
    field: 'SuccessfulCalls',
    headerTooltip: 'SuccessfulCalls',
    tooltipField: 'SuccessfulCalls',
    width: 110,
    maxWidth: 110,
    minWidth: 110,
  },
  {
    headerName: 'Actions',
    field: 'Actions',
    headerTooltip: 'Actions',
    cellRenderer: ActionFormatter,
    width: 70,
    resizable: false,
    wrapText: true,
    autoHeight: true,
  },
]

const hasDocumentation = ['Portal', 'OAuth', 'DocumentPreview', 'OfficeOnline', 'Indexing', 'Sharing']

export const getSettingsColumnDefs = (history: History): ColDef[] => [
  iconCol,
  {
    headerName: 'Display Name',
    field: 'DisplayName',
    headerTooltip: 'Display Name',
    tooltipField: 'DisplayName',
    sortable: true,
    resizable: true,
    flex: 1,
  },
  {
    headerName: 'Description',
    field: 'Description',
    headerTooltip: 'Description',
    tooltipField: 'Description',
    resizable: true,
    flex: 4,
  },
  getBinaryEditColumn(history, PATHS.configuration.appPath),
  {
    headerName: 'Learn More',
    resizable: false,
    cellRenderer: (params: { data: UpdatedSettings }) =>
      hasDocumentation.includes(params.data.Name.replace(/\.settings/gi, '')) ? (
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`${SETUP_DOCS_URL}${createAnchorFromName(params.data.Name)}`}>
          <IconButton style={{ padding: '3px', marginBottom: '6px' }}>
            <InfoOutlined style={{ fontSize: '16px' }} />
          </IconButton>
        </a>
      ) : null,
    width: 90,
  },
  {
    headerName: 'Actions',
    field: 'Actions',
    headerTooltip: 'Actions',
    cellRenderer: ActionFormatter,
    width: 70,
    resizable: false,
  },
]

export const searchColumnDefs: ColDef[] = [
  checkBoxCol,
  iconCol,
  {
    headerName: 'Display Name',
    field: 'DisplayName',
    headerTooltip: 'Display Name',
    tooltipField: 'DisplayName',
    flex: 5,
    filter: true,
    sortable: true,
    comparator: (valueA: string, valueB: string) => {
      return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
    },
    resizable: true,
  },
  {
    headerName: 'Path',
    field: 'Path',
    headerTooltip: 'Path',
    tooltipField: 'Path',
    flex: 5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Modified By',
    field: 'ModifiedBy',
    headerTooltip: 'Modified By',
    tooltipField: 'ModifiedBy',
    cellRenderer: UserNameFormatter,
    flex: 1.5,
    filter: true,
    sortable: true,
    resizable: true,
  },
  {
    headerName: 'Actions',
    field: 'Actions',
    headerTooltip: 'Actions',
    cellRenderer: ActionFormatter,
    width: 70,
    resizable: false,
    wrapText: true,
    autoHeight: true,
  },
]
